<script lang="ts">
  // @ts-nocheck
  import { onDestroy, onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import FormWrapper, { type FormContext } from "@kontextwerk/web-sdk/ui/layout/FormWrapper.svelte"
  import {
    removeAllErrors,
    validateField,
    validateInput,
  } from "@kontextwerk/web-sdk/core/forms/formValidation"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    classifyPhoneNumber,
    createContactPoint,
  } from "../../../../../../../api/kehrblatt/customer-registry/contact-points"
  import { createCustomer } from "../../../../../../../api/kehrblatt/customer-registry/customers"
  import { createPropertyCustomer } from "../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
  import { mdiClose } from "@mdi/js"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import {
    defaultChannelPriorities,
    normalizeChannelPriorities,
  } from "../../../../../_shared/_logic/channel-priorities"
  import {
    defaultOutreachEnabledForRole,
    normalizePropertyCustomerRole,
    propertyCustomerRoleOptions,
  } from "../../../../../_shared/_logic/customer-roles"
  import PropertyUnitAsyncMultiSelect from "../property-unit-async-multi-select"
  import { emitEvent } from "../../../../../../_shared/_logic/eventbus"
  import type { Snippet } from "svelte"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    onCreated?: ((customer: Customer) => void) | null
  }

  type CustomerFormModel = ReturnType<typeof resetForm>

  const customerTypeOptions = [
    { name: "Privatperson", value: "PERSON" },
    { name: "Unternehmen", value: "COMPANY" },
  ]

  const fieldIds = {
    displayName: "customer-display-name",
    type: "customer-type",
    notes: "customer-notes",
    unitSelection: "customer-unit-selection",
    role: "customer-unit-role",
    email: "customer-email",
    phone: "customer-phone",
    phoneKind: "customer-phone-kind",
  }

  const resetForm = (): CustomerFormModel => ({
    display_name: "",
    type: "PERSON",
    channel_priorities: defaultChannelPriorities(),
    notes: "",
    unit_selection: [],
    role: "TENANT",
    email: "",
    phone: "",
    phone_kind: "FIXED_LINE" as "MOBILE" | "FIXED_LINE",
  })

  let {
    organizationId,
    onCreated = null,
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  const close = (submitting: boolean) => {
    if (submitting) return

    onremoveOverlay?.()
  }

  let submitting = $state(false)
  let submitForm = $state<(() => Promise<void>) | null>(null)
  let classificationRequest = 0
  let classificationPromise: Promise<void> | null = null
  let classificationPending = $state(false)
  let classificationHint = $state<string | null>(null)
  let phoneKindManuallySelected = false

  const inputValue = (value: unknown) => {
    if (value instanceof Event) {
      const target = value.target as HTMLInputElement | HTMLTextAreaElement | null

      return `${target?.value ?? ""}`
    }

    return `${value ?? ""}`
  }

  const handlePhoneKindChange = () => {
    classificationRequest += 1
    classificationPromise = null
    classificationPending = false
    classificationHint = null
    phoneKindManuallySelected = true
  }

  const classifyChangedPhone = async (data: CustomerFormModel, rawValue: unknown) => {
    const value = inputValue(rawValue).trim()
    data.phone = value
    const requestId = ++classificationRequest

    if (!value) {
      classificationPending = false
      classificationHint = null

      return
    }

    classificationPending = true
    try {
      const result = await classifyPhoneNumber(value)

      if (requestId !== classificationRequest || data.phone.trim() !== value) return

      if (!phoneKindManuallySelected) data.phone_kind = result.suggested_kind

      classificationHint =
        result.confidence === "HIGH"
          ? `Erkannt als ${result.suggested_kind === "MOBILE" ? "Mobilnummer" : "Festnetznummer"}.`
          : "Nicht eindeutig – Festnetz ist vorausgewählt. Bitte prüfen."
    } catch {
      if (requestId === classificationRequest) classificationHint = null
    } finally {
      if (requestId === classificationRequest) classificationPending = false
    }
  }

  const queuePhoneClassification = (data: CustomerFormModel, rawValue: unknown) => {
    classificationPromise = classifyChangedPhone(data, rawValue)
    void classificationPromise
  }

  const validateCustomer = ({
    data,
    form,
  }: {
    data: CustomerFormModel
    form: HTMLFormElement | null
  }) => {
    removeAllErrors(form ?? undefined)
    let isValid = true

    if (!validateField(fieldIds.displayName, data.display_name, validateInput)) {
      isValid = false
    }

    const email = data.email.trim()

    if (email && !validateField(fieldIds.email, email, validateEmail)) {
      isValid = false
    }

    const phone = data.phone.trim()

    if (phone && !validateField(fieldIds.phone, phone, validatePhone)) {
      isValid = false
    }

    return isValid
  }

  const validateEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
      ? null
      : "Bitte eine gültige E-Mail-Adresse angeben."

  const isPlausiblePhone = (value: string): boolean => {
    const trimmed = value.trim()

    if (!trimmed) return false

    if (!/^[\d+().\s-]+$/.test(trimmed)) return false

    const digitsOnly = trimmed.replace(/\D/g, "")

    return digitsOnly.length >= 6 && digitsOnly.length <= 15
  }

  const phoneErrorMessage =
    "Bitte eine gültige Telefonnummer angeben (mind. 6 Ziffern, nur Zahlen, +, (), -)."

  const validatePhone = (value: string) => (isPlausiblePhone(value) ? null : phoneErrorMessage)

  const keepSingleUnitSelection = (values: string[], data: CustomerFormModel) => {
    const normalizedValues = Array.from(
      new Set((values ?? []).map((value) => value.trim()).filter(Boolean)),
    )
    data.unit_selection = normalizedValues.length
      ? [normalizedValues[normalizedValues.length - 1]]
      : []
  }

  const createOptionalContactPoint = async (
    customer: Customer,
    kind: "EMAIL" | "MOBILE" | "FIXED_LINE",
    value: string,
  ) => {
    const normalizedValue = value.trim()

    if (!normalizedValue) return null

    return createContactPoint({
      kind,
      label: null,
      value: normalizedValue,
      is_verified: false,
      source: "MANUAL",
      customer_id: customer.id,
      organization_id: organizationId,
    })
  }

  const linkSelectedUnit = async (customer: Customer, data: CustomerFormModel) => {
    const selectedUnitValue = data.unit_selection?.[0]?.trim()

    if (!selectedUnitValue) return null

    const [propertyId, unitIdRaw] = selectedUnitValue.split("::")
    const unitId = unitIdRaw?.trim()

    if (!propertyId || !unitId) {
      newNotification({
        class: "error",
        html: "Bitte eine gespeicherte Einheit auswählen, wenn der Ansprechpartner direkt verknüpft werden soll.",
      })

      return null
    }

    const role = normalizePropertyCustomerRole(data.role)

    return createPropertyCustomer({
      property_id: propertyId,
      property_unit_id: unitId,
      customer_id: customer.id,
      role,
      outreach_enabled: defaultOutreachEnabledForRole(role),
      is_primary: false,
    })
  }

  const emitCustomerCreated = (customer: Customer) => {
    emitEvent("customer-registry.changed", {
      organizationId,
      entity: "customer",
      customerId: customer.id,
    })
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })

  const saveCustomer = async (data: CustomerFormModel) => {
    if (!organizationId) return

    await classificationPromise
    const payload: CustomerCreatePayload = {
      display_name: data.display_name.trim(),
      type: data.type || "PERSON",
      channel_priorities: normalizeChannelPriorities(data.channel_priorities),
      notes: data.notes.trim() ? data.notes.trim() : null,
      organization_id: organizationId,
    }
    try {
      const created = await createCustomer(payload)
      try {
        await Promise.all([
          createOptionalContactPoint(created, "EMAIL", data.email),
          createOptionalContactPoint(created, data.phone_kind, data.phone),
          linkSelectedUnit(created, data),
        ])
      } catch (err) {
        console.error("customer_create_related_data_failed", err)
        emitCustomerCreated(created)
        onCreated?.(created)
        newNotification({
          class: "error",
          html: `Ansprechpartner <strong>${created.display_name}</strong> wurde angelegt, aber Einheit oder Kontaktdaten konnten nicht vollständig gespeichert werden.`,
        })
        onremoveOverlay?.()

        return
      }
      emitCustomerCreated(created)
      onCreated?.(created)
      newNotification({
        class: "success",
        html: `Ansprechpartner <strong>${created.display_name}</strong> wurde angelegt.`,
      })
      onremoveOverlay?.()
    } catch (err) {
      console.error("customer_create_failed", err)
      newNotification({ class: "error", html: "Ansprechpartner konnte nicht angelegt werden." })
    }
  }
</script>

<div class="overlay-form">
  <FormWrapper
    theme="dark"
    mode="create"
    getInitObject={resetForm}
    validateData={validateCustomer}
    onSubmit={saveCustomer}
    submitLabel="Anlegen"
    showReset={false}
    hideSubmit={Boolean(setActionButtonSnippet)}
    reinitKey={organizationId}
    formSnippet={formContent}
    bind:submitting
    setSubmitFn={(fn) => (submitForm = fn)}
  />
</div>

{#snippet formContent(ctx: FormContext)}
  {@const { data, submitting } = ctx}
  <section class="row">
    <Input
      surfaceTone="light"
      id={fieldIds.type}
      type="select"
      placeholder="Typ"
      options={customerTypeOptions}
      bind:value={data.type}
      brightBackground={true}
      disabled={submitting}
    />
    <Input
      surfaceTone="light"
      brightBackground={true}
      id={fieldIds.displayName}
      placeholder="Anzeigename"
      bind:value={data.display_name}
      disabled={submitting}
      tooltip="Der Ansprechpartner wird beim Terminvereinbaren über diesen Namen verifiziert. Stellen Sie sicher, dass er korrekt ist."
    />
    <Input
      surfaceTone="light"
      id={fieldIds.role}
      type="select"
      placeholder="Rolle zur Einheit"
      options={propertyCustomerRoleOptions}
      bind:value={data.role}
      brightBackground={true}
      disabled={submitting}
    />
  </section>
  <section class="row">
    <Input
      surfaceTone="light"
      brightBackground={true}
      id={fieldIds.notes}
      placeholder="Notiz (optional)"
      type="textarea"
      bind:value={data.notes}
      disabled={submitting}
    />
  </section>
  <section class="row">
    <PropertyUnitAsyncMultiSelect
      id={fieldIds.unitSelection}
      {organizationId}
      placeholder="Einheit verknüpfen (optional)"
      bind:value={data.unit_selection}
      brightBackground={true}
      disabled={submitting}
      onChange={(values) => keepSingleUnitSelection(values, data)}
    />
  </section>
  <section class="row">
    <Input
      surfaceTone="light"
      brightBackground={true}
      id={fieldIds.email}
      placeholder="E-Mail (optional)"
      bind:value={data.email}
      disabled={submitting}
    />
    <Input
      surfaceTone="light"
      brightBackground={true}
      id={fieldIds.phone}
      placeholder="Telefonnummer (optional)"
      bind:value={data.phone}
      disabled={submitting}
      onChange={(value) => queuePhoneClassification(data, value)}
    />
    <Input
      surfaceTone="light"
      id={fieldIds.phoneKind}
      type="select"
      placeholder="Telefonart"
      options={[
        { name: "Mobil", value: "MOBILE" },
        { name: "Festnetz", value: "FIXED_LINE" },
      ]}
      bind:value={data.phone_kind}
      brightBackground={true}
      disabled={submitting || !data.phone.trim()}
      onChange={() => handlePhoneKindChange(data)}
    />
  </section>
  {#if classificationPending || classificationHint}
    <p class="classification-hint" aria-live="polite">
      {classificationPending ? "Nummer wird geprüft …" : classificationHint}
    </p>
  {/if}
{/snippet}

{#snippet actionButtons()}
  <CTA
    darkMode={false}
    type="secondaryButton"
    content="Abbrechen"
    onClick={() => close(submitting)}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiClose} />
    {/snippet}
  </CTA>
  <CTA
    darkMode={false}
    type="primaryButton"
    content={submitting ? "Speichere…" : "Anlegen"}
    onClick={() => submitForm?.()}
    disabled={submitting}
  />
{/snippet}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .overlay-form {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    height: fit-content;
    gap: 1rem;
    padding-bottom: 1.2rem;
  }

  :global(.overlay-form .formActions) {
    display: none;
  }

  .classification-hint {
    margin: -0.65rem 0 0;
    color: #74808c;
    font-size: 0.82rem;
    line-height: 1.35;
  }
</style>
